"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { X, CheckCircle } from "lucide-react"

const SelectWebAppModal = ({ onClose }) => {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [purpose, setPurpose] = useState("")
  const [selectedApp, setSelectedApp] = useState("")
  const [error, setError] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [filteredApps, setFilteredApps] = useState([])

  const webapps = [
    {
      value: "find-calling",
      label: "Find your calling copilot",
      desc: "Discover your unique path and purpose through deep insights.",
      category: "personal",
    },
    {
      value: "business-strategy",
      label: "Business strategy copilot",
      desc: "Refine your business ideas and align them with powerful strategy.",
      category: "business",
    },
    {
      value: "find-speaker",
      label: "Find your speaker",
      desc: "Clarify your core message and craft impactful presentations.",
      category: "communication",
    },
    {
      value: "value-ladder",
      label: "Value Ladder copilot",
      desc: "Design a value ladder with five essential price points.",
      category: "business",
    },
    {
      value: "value-proposition",
      label: "Value Proposition copilot",
      desc: "Design a value proposition with five essential price points.",
      category: "business",
    },
    {
      value: "target-market",
      label: "Target Market copilot",
      desc: "Design a value proposition with five essential price points.",
      category: "business",
    },
    {
      value: "traffic-sources",
      label: "Traffic Sources copilot",
      desc: "Design a value proposition with five essential price points.",
      category: "marketing",
    },
    {
      value: "irresistible-copilot",
      label: "Irresistible Offer Copilot",
      desc: "Design a value proposition with five essential price points.",
      category: "marketing",
    },
    {
      value: "findconsultingniche-copilot",
      label: "Find your consulting niche",
      desc: "Design a consulting niche",
      category: "marketing",
    },
    {
      value: "findyourcoaching-copilot",
      label: "Find your coaching niche",
      desc: "Design a Coaching Niche",
      category: "marketing",
    },
    {
      value: "brand-story",
      label: "Brand Story Creator",
      desc: "Craft compelling brand narratives that resonate with your audience.",
      category: "branding",
    },
    {
      value: "digital-product",
      label: "Digital Product Concepting",
      desc: "Develop innovative digital product concepts with AI assistance.",
      category: "product",
    },
    {
      value: "chat-bot",
      label: "Chat Bot Creator",
      desc: "Create a chatbot for your website or app.",
      category: "chatbot",
    },
    {
      value: "masterbot",
      label: "Master Bot Creator",
      desc: "Paste whole conversation and start chatting with it.",
      category: "masterbot",
    },
    {
      value: "aiworkflow",
      label: "AI workflow creator",
      desc: "Ai workflow creator",
      category: "aiworkflow",
    },
  ]

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredApps(webapps)
    } else {
      const filtered = webapps.filter(
        (app) =>
          app.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          app.desc.toLowerCase().includes(searchQuery.toLowerCase()) ||
          app.category.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      setFilteredApps(filtered)
    }
  }, [searchQuery])

  useEffect(() => {
    setFilteredApps(webapps)

    // Add event listener to close modal on escape key
    const handleEscKey = (e) => {
      if (e.key === "Escape") {
        onClose()
      }
    }

    document.addEventListener("keydown", handleEscKey)
    return () => document.removeEventListener("keydown", handleEscKey)
  }, [onClose])

  const handleSubmit = () => {
    if (!selectedApp) {
      setError("Please select a web app.")
      return
    }

    // For chat-based tools, navigate to chat page
    if (selectedApp === "brand-story" || selectedApp === "digital-product" || selectedApp === "chat-bot") {
      navigate("/chat", {
        state: {
          email: user.email,
          purpose: "testing",
          type: selectedApp,
        },
      })
    } else {
      // For questionnaire-based tools, navigate to questions page
      navigate("/questions", {
        state: {
          email: user.email,
          purpose,
          type: selectedApp,
        },
      })
    }

    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-gray-900 rounded-2xl w-[95%] max-w-2xl p-6 relative border border-gray-800 shadow-xl animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-800 transition-colors"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-6">
          <h2 className="text-white text-2xl font-semibold mb-2">Select Your Copilot</h2>
          <p className="text-gray-400 text-sm">Choose the AI experience that best fits your current journey</p>
        </div>

        <div className="text-white text-sm space-y-3 mb-6 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
          {filteredApps.length > 0 ? (
            filteredApps.map((app) => (
              <div
                key={app.value}
                className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                  selectedApp === app.value
                    ? "bg-purple-500/20 border border-purple-500/50"
                    : "hover:bg-gray-800/70 border border-transparent"
                }`}
                onClick={() => setSelectedApp(app.value)}
              >
                <div className="mt-1">
                  {selectedApp === app.value ? (
                    <CheckCircle className="h-5 w-5 text-purple-400" />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-gray-600"></div>
                  )}
                </div>
                <div>
                  <div className="flex items-center">
                    <span className="font-medium">{app.label}</span>
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400">
                      {app.category}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">{app.desc}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4 text-gray-400">No copilots found matching your search.</div>
          )}
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-500/50 text-red-200 text-sm p-3 rounded-lg mb-4">{error}</div>
        )}

        <button
          onClick={handleSubmit}
          className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 ${
            selectedApp
              ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg hover:shadow-purple-500/25"
              : "bg-gray-700 text-gray-400 cursor-not-allowed"
          }`}
          disabled={!selectedApp}
        >
          Continue
        </button>
      </div>
    </div>
  )
}

export default SelectWebAppModal
